// 2026-09-30 既定 = step + open（運ぶ間 walk_reach・止まる間 walk_passing）+ 1歩 170ms・前へ3歩ごとに +300ms の一息
//   (KNIGHT_WALK_DEFAULT)。ユーザー 10-01c W-1「判定: ステップ・開いて出て寄せる」。旧の既定 (rest-open・一息なし) = DEV `?knightWalkPose=legacy`。
//   `?knightWalkPose=` を付けた URL は比較パックどおり URL の値だけで決まる (例 `?knightWalkPose=open` 単独 = 10-01b の「開いて進む」)。
// Original foot measurements below describe DEV old scale; helmet default table is remeasured below.
// 騎士の「待機中の歩き」(間合いを詰める歩き・近すぎる時の後退) の足運び (2026-09-27)。
//   ユーザー逐語 (2026-09-27 検収パック R-3):「間合いは良いが、不自然にスーッと移動するのがおかしい。
//     絵も変わらずにスライド移動しているのが変」。気になるのは「離れる時の後ずさり」と「近づく時の歩き」の両方。
//
// 実測 (playlogs 2026-09-27 210629 の通常戦 + 実機の描画矩形・`_確認用/2026-09-27-knight-move/`):
//   - 近づく歩き = 148px/s (walkSpeed 270×0.55)。歩きの絵は「1回の移動で 130px 進んでから」しか出ない
//     (sprites.ts walkPoseGateOpen)。実戦の近づきは 1回 42〜181px なので、大半が**立ち絵のまま滑っていた**。
//     歩きの絵が出ても 1枚を 65px 持ったまま進む (絵の歩幅 74px と合っていない)。
//   - 離れる後退 = 297px/s (立て直し BOSS.overlapRegainSpeedMul 2.0)。1回 29〜90px で必ず 130px 未満 =
//     **毎回、立ち絵のまま 0.1〜0.3 秒で後ろへ滑っていた**。
//
// 足の位置 (本体の前方 + / 後方 -、ゲーム px・displayHeight 216・床から 7% の帯の足の中心):
//   idle         = 前足 +20.9 / 後ろ足 +3.1 (足を揃えた立ち姿)
//   walk_passing = 前足 +9.2 / 後ろ足 -18.6 (足を揃えかけた絵)
//   walk_reach   = 前足 +16.6 / 後ろ足 -57.4 (前足を着いた広い構え・後ろ足はつま先)
//   前へ1歩 = 「今の構えの前足」が「次の構えの後ろ足」になる = 体の動く量 = 今の前足 − 次の後ろ足。
//     広い構え→広い構え 74.0px / 広い構え→揃えかけ 35.2px / 揃えかけ→広い構え 66.6px / 立ち姿→広い構え 78.3px。
//   後ろへ1歩 = 「今の構えの後ろ足」が「次の構えの前足」になる = 次の前足 − 今の後ろ足。
//     広い構え→広い構え 74.0px / 立ち姿→広い構え 13.5px (後ろ足を引いて構えるだけ)。
//   → 止まって立っている絵どうしでは、着いた足が床の同じ点に来る (1px も滑らない)。
//   出所 = PNG の不透明画素の実測 (tests/knight_gait.test.ts が PNG から測り直して照合する)。
//
// 切り替え (DEV のみ URL・配布版は既定): `?knightMove=old|walk|step|snap`
//   step (既定・推奨) = 踏みしめ歩き。構えで止まって立ち、1歩を運んで次の構えで止まる。止まっている間は足が滑らない。
//          運んでいる約 0.3 秒だけ揃えかけの絵 (walk_passing)。平均の速さは今と同じ。後退も同じ足運び (旧の 2倍速より遅い 1.5倍速)。
//          好みの間合いの手前では揃えかけの構えへ半歩 (35px) で寄せて止まる。
//   snap = 比較用の紙芝居歩き。画像の保持中は体を動かさず、切替 tick にだけ足位置を合わせて移動。
//          広い構えと揃えかけを交互に見せる。画像の保持時間 = 次の移動量 ÷ 歩く速さ。上下移動なし。
//   walk = 歩幅に合わせた連続歩き。速さは今までどおり一定のまま、絵を 35/39px ごとに切り替える (足は ±19px 往復)。
//          後退は歩く速さ (旧の半分)。
//   old  = 変更前 (130px 進むまで立ち絵・後退は 2倍速の立ち絵)。
import { knightHelmetScaleEnabled } from './knight_helmet_scale'
import { knightRestPoseKey } from './knight_rest'
import type { Boss } from './types'
import { FIXED_DT } from './balance'
import { knightSuriGlideEase } from './knight_suri_dust'

export type KnightMove = 'old' | 'walk' | 'step' | 'snap' | 'suri'
const MOVE_IDS: readonly KnightMove[] = ['old', 'walk', 'step', 'snap', 'suri']
let mode: KnightMove = 'step'
export function getKnightMove(): KnightMove { return mode }
export function setKnightMove(v: KnightMove): void { mode = MOVE_IDS.includes(v) ? v : 'step' }
export const KNIGHT_MOVE_LABELS: Record<KnightMove, string> = {
  step: '踏みしめ歩き（構えで止まり、1歩ずつ運ぶ）',
  snap: '紙芝居歩き（絵が変わる瞬間だけ体が動く・滑らない）',
  walk: '歩幅に合わせた連続歩き',
  old: '変更前（立ち絵のまま滑る・後退は速い）',
  suri: 'すり足（腰を落とし、前足を滑らせ後ろ足を寄せる）',
}
export const KNIGHT_SURI_DEFAULT = { stepPx: 50, glideMs: 200, restMs: 140 } as const
let suriSettings: { stepPx: number; glideMs: number; restMs: number } = { ...KNIGHT_SURI_DEFAULT }
export function getKnightSuriSettings() { return { ...suriSettings } }
export function setKnightSuriSettings(settings: Partial<typeof suriSettings>): void {
  suriSettings = {
    stepPx: Number.isFinite(settings.stepPx) && settings.stepPx! > 0 ? settings.stepPx! : KNIGHT_SURI_DEFAULT.stepPx,
    glideMs: Number.isFinite(settings.glideMs) && settings.glideMs! > 0 ? settings.glideMs! : KNIGHT_SURI_DEFAULT.glideMs,
    restMs: Number.isFinite(settings.restMs) && settings.restMs! >= 0 ? settings.restMs! : KNIGHT_SURI_DEFAULT.restMs,
  }
}

export type KnightStance = 'idle' | 'walk_passing' | 'walk_reach'
// 足の位置 (上の表)。値を変えたら tests/knight_gait.test.ts が PNG と照合する。
export const KNIGHT_WALK_FOOT_OLD_PX: Record<KnightStance, { front: number; back: number }> = {
  idle: { front: 20.9, back: 3.1 },
  walk_passing: { front: 9.2, back: -18.6 },
  walk_reach: { front: 16.6, back: -57.4 },
}
// Helmet-fit PNG foot band: passing 9.4615/-19.1933, reach 17.2935/-59.8065 px.
// Rounded to 0.1 px; default full stride 77.1, idle first step 80.7, half step 36.5.
export const KNIGHT_WALK_FOOT_PX: Record<KnightStance, { front: number; back: number }> = {
  idle: { front: 20.9, back: 3.1 },
  walk_passing: { front: 9.5, back: -19.2 },
  walk_reach: { front: 17.3, back: -59.8 },
}
export function knightWalkFootPx() { return knightHelmetScaleEnabled() ? KNIGHT_WALK_FOOT_PX : KNIGHT_WALK_FOOT_OLD_PX }
// PNG helmet top 8% band; same bbox centre and draw scale as the foot table.
export const KNIGHT_WALK_HELMET_PX: Record<KnightStance, number> = {
  idle: 13.8, walk_passing: -1.5, walk_reach: -9.4,
}
export const KNIGHT_WALK_HELMET_OLD_PX: Record<KnightStance, number> = {
  idle: 13.8, walk_passing: -1.5, walk_reach: -9.0,
}
export function knightWalkHelmetPx() { return knightHelmetScaleEnabled() ? KNIGHT_WALK_HELMET_PX : KNIGHT_WALK_HELMET_OLD_PX }
export type KnightWalkPose = 'rest-open' | 'open' | 'reach'
export type KnightWalkAnchor = 'body' | 'feet'
export const KNIGHT_WALK_DEFAULT = { pose: 'open', moveMs: 170, burst: 3, burstRestMs: 300, anchor: 'body' } as const
let walkPose: KnightWalkPose = KNIGHT_WALK_DEFAULT.pose
let walkAnchor: KnightWalkAnchor = KNIGHT_WALK_DEFAULT.anchor
export function getKnightWalkPose(): KnightWalkPose { return walkPose }
export function setKnightWalkPose(v: string | null): void {
  walkPose = v === 'open' || v === 'reach' || v === 'rest-open' ? v : v === 'legacy' ? 'rest-open' : KNIGHT_WALK_DEFAULT.pose
}
export function getKnightWalkAnchor(): KnightWalkAnchor { return walkAnchor }
export function setKnightWalkAnchor(v: KnightWalkAnchor): void { walkAnchor = v === 'feet' ? 'feet' : 'body' }
function openWalk(): boolean { return mode === 'step' && walkPose === 'open' }
function reachWalk(): boolean { return mode === 'step' && walkPose === 'reach' }
let quickMoveMs: number | undefined = KNIGHT_WALK_DEFAULT.moveMs
let burstSteps: number | undefined = KNIGHT_WALK_DEFAULT.burst
let burstRestMs: number = KNIGHT_WALK_DEFAULT.burstRestMs
export function setKnightStepMoveComparison(moveMs?: number, burst?: number, restMs?: number): void {
  quickMoveMs = Number.isFinite(moveMs) && moveMs! > 0 ? moveMs : undefined
  burstSteps = Number.isInteger(burst) && burst! > 0 ? burst : undefined
  burstRestMs = Number.isFinite(restMs) && restMs! >= 0 ? restMs! : 300
}
export function getKnightStepMoveComparison() { return { moveMs: quickMoveMs, burst: burstSteps, burstRestMs } }
export function resetKnightWalkDefaults(): void {
  walkPose = KNIGHT_WALK_DEFAULT.pose
  walkAnchor = KNIGHT_WALK_DEFAULT.anchor
  setKnightStepMoveComparison(KNIGHT_WALK_DEFAULT.moveMs, KNIGHT_WALK_DEFAULT.burst, KNIGHT_WALK_DEFAULT.burstRestMs)
}
function stepComparisonEnabled(): boolean { return mode === 'step' && (quickMoveMs !== undefined || burstSteps !== undefined) }
export function knightNaturalStridePx(): number {
  const foot = knightWalkFootPx().walk_reach
  return foot.front - foot.back
}
let forwardStride: number | undefined
let forwardTempo: number | undefined
export function setKnightStepComparison(stride?: number, tempoMs?: number): void {
  forwardStride = Number.isFinite(stride) && stride! > 0 ? stride : undefined
  forwardTempo = Number.isFinite(tempoMs) && tempoMs! > 0 ? tempoMs : undefined
}
function forwardStridePx(): number { return forwardStride ?? knightNaturalStridePx() }
/** 前へ1歩の体の動く量 (今の前足 → 次の後ろ足)。 */
export function knightStepForwardPx(from: KnightStance, to: KnightStance): number {
  return knightWalkFootPx()[from].front - knightWalkFootPx()[to].back
}
/** 後ろへ1歩の体の動く量 (今の後ろ足 → 次の前足)。 */
export function knightStepBackPx(from: KnightStance, to: KnightStance): number {
  return knightWalkFootPx()[to].front - knightWalkFootPx()[from].back
}
/** Default helmet-fit full stride = 77.1 px (old-scale comparison = 74 px). */
export const KNIGHT_STEP_PX = knightStepForwardPx('walk_reach', 'walk_reach')
/** 連続歩き (walk) で walk_reach を見せる距離 = 広い構え→揃えかけの前への1歩 (35.2px)。残り (38.8px) が walk_passing。 */
export const KNIGHT_WALK_REACH_HOLD_PX = knightStepForwardPx('walk_reach', 'walk_passing')

// 踏みしめ歩き (step) の時間。1歩の周期 = 歩幅 ÷ 歩く速さ (148px/s なら 0.50 秒) を「運ぶ 64%・止まる 36%」に割る。
//   平均の速さは今と同じ = 間合いに着くまでの時間は変わらない。後退の速さは下の stepBackSpeedMul。
// Forward comparison deliberately allows planted-foot drift for stride > natural; no correction.
const STEP_MOVE_SHARE = 0.64
// 後退 (立て直し) の速さ = 歩く速さ × 1.5 (約 3歩/秒)。旧は 2倍速の立ち絵滑り。
//   bot 比較 (べったり押し込み・4 seed・2026-09-27): 芯の中にいる割合 旧 66.7% / ×0.85 89.4% / ×1.5 72.2% / ×2.0 76.1%。
//   0.85 では押し込まれたまま抜けられず重なりが増えた (09-18 の立て直しの目的に反する) ので 1.5。DEV `?knightBack=<倍率>` で比較。
const STEP_BACK_SPEED_MUL_DEFAULT = 1.5
let stepBackSpeedMul = STEP_BACK_SPEED_MUL_DEFAULT
export function getKnightStepBackSpeedMul(): number { return stepBackSpeedMul }
export function setKnightStepBackSpeedMul(v: number): void {
  stepBackSpeedMul = Number.isFinite(v) && v > 0 ? v : STEP_BACK_SPEED_MUL_DEFAULT
}
// 止まったまま次の歩きが来なければ立ち絵へ戻すまでの時間 (止まる時間より長く = 歩きの途中で立ち絵を挟まない)。
const STEP_IDLE_AFTER_MS = 420
const WALK_IDLE_AFTER_MS = 120

export type KnightGait = {
  /** 歩き始めから進んだ量 (向いている方向が +)。連続歩き (walk) の絵を選ぶ。 */
  s: number
  /** 歩きの絵を出している間 true。 */
  walking: boolean
  /** 止まってからの ms (step は1歩の着地から・walk は最後に動いた tick から)。 */
  restMs: number
  /** 踏みしめ歩きで、いま立っている構え (idle = 立ち姿)。 */
  stance?: KnightStance
  /** 踏みしめ歩きの運んでいる最中の1歩。 */
  step?: { fromX: number; toX: number; elapsedMs: number; durMs: number; to: KnightStance; from?: KnightStance; backward?: boolean }
  /** DEV ステップ移動の比較 (?knightStepMoveMs / ?knightStepBurst): 同じ向きに続けた歩数・向き・次の1歩までに止まる ms。 */
  stepCount?: number
  stepWorldDir?: 1 | -1
  stepBackward?: boolean
  stepRestNeededMs?: number
  /** 紙芝居歩き: 現在の画像を保持した時間。 */
  snapHoldMs?: number
  /** 紙芝居歩き: 最後の画像切替方向。 */
  snapBackward?: boolean
  suri?: { phase: 'glide' | 'draw'; fromX: number; toX: number; elapsedMs: number; durMs: number; backward: boolean }
}

export type KnightGaitWant = {
  /** 動く向き (世界の x の符号)。 */
  worldDir: 1 | -1
  backward: boolean
  /** 進みたい残り (近づく = 好みの間合いまで / 後退 = 止まる間合いまで)。 */
  remainingPx: number
  /** 最低これだけは進みたい (攻撃に入れる距離まで)。0 以下 = 無し。 */
  mustPx: number
  /** 歩く速さ (px/s)。 */
  speed: number
}

export function usesKnightGait(boss: Pick<Boss, 'id'>): boolean {
  return boss.id === 'vigil-knight' && mode !== 'old'
}

/** 待機を抜けたら歩きの状態を捨てる (技の途中で持ち越さない)。 */
export function prepareKnightGait(boss: Boss): Boss {
  if (!boss.knightGait || ((boss.state === 'idle' || boss.state === 'beatGap') && usesKnightGait(boss))) return boss
  const { knightGait: _g, ...rest } = boss
  return rest
}

const smoothstep = (p: number): number => { const c = Math.max(0, Math.min(1, p)); return c * c * (3 - 2 * c) }

/** 踏みしめ歩きの次の1歩 (行き先の構えと長さ)。null = 出さない。 */
export function chooseKnightStep(stance: KnightStance, want: KnightGaitWant): { to: KnightStance; px: number } | null {
  if (openWalk() || reachWalk()) {
    const to: KnightStance = reachWalk() ? 'walk_reach' : 'walk_passing'
    if (want.backward) return want.remainingPx > 0.5 ? { to, px: knightNaturalStridePx() } : null
    const full = forwardStridePx(), half = full / 2
    if (want.remainingPx >= full - 10) return { to, px: full }
    if (want.remainingPx >= half * 0.7) return { to, px: half }
    if (want.mustPx > 0) return { to, px: half >= want.mustPx ? half : full }
    return null
  }
  if (want.backward) {
    // 後退は広い構えで1歩ずつ (立ち姿からは後ろ足を引いて構えるだけの 13.5px)。止まる間合いを越えるまで出す。
    if (want.remainingPx <= 0.5) return null
    return { to: 'walk_reach', px: knightStepBackPx(stance, 'walk_reach') }
  }
  const k = forwardStridePx() / knightNaturalStridePx()
  const full = knightStepForwardPx(stance, 'walk_reach') * k
  const half = knightStepForwardPx(stance, 'walk_passing') * k
  // 好みの間合いを 10px より深く越えない範囲なら1歩。足りなければ揃えかけへ半歩。
  if (want.remainingPx >= full - 10) return { to: 'walk_reach', px: full }
  if (half > 0 && want.remainingPx >= half * 0.7) return { to: 'walk_passing', px: half }
  // 攻撃に入れる距離の外なら、届くまで (半歩で届けば半歩)。
  if (want.mustPx > 0) return half > 0 && half >= want.mustPx ? { to: 'walk_passing', px: half } : { to: 'walk_reach', px: full }
  return null
}

/**
 * 1 tick 進める。返り値の x は壁・主人公の芯でのクランプ前。呼び出し側がクランプして `settleKnightGait` で戻す。
 * blocksAttack = 踏みしめ歩きで1歩を運んでいる最中 (着いてから技に入る = 足が浮いたまま構えない)。
 */
export function advanceKnightGait(boss: Boss, want: KnightGaitWant | null): { x: number; gait: KnightGait; blocksAttack: boolean } {
  const dtMs = FIXED_DT * 1000
  const prev: KnightGait = boss.knightGait ?? { s: 0, walking: false, restMs: 0 }
  if (mode === 'suri') {
    const phase = prev.suri
    if (phase?.phase === 'glide') {
      const elapsedMs = Math.min(phase.durMs, phase.elapsedMs + dtMs)
      const x = phase.fromX + (phase.toX - phase.fromX) * knightSuriGlideEase(elapsedMs / phase.durMs)
      if (elapsedMs >= phase.durMs) return { x, gait: { s: prev.s, walking: true, restMs: 0,
        suri: { ...phase, phase: 'draw', elapsedMs: 0,
          durMs: suriSettings.restMs / (phase.backward ? stepBackSpeedMul : 1) } }, blocksAttack: false }
      return { x, gait: { ...prev, suri: { ...phase, elapsedMs } }, blocksAttack: true }
    }
    const elapsedMs = phase?.phase === 'draw' ? phase.elapsedMs + dtMs : 0
    const needed = Math.max(0, want?.mustPx ?? 0)
    const remaining = want?.remainingPx ?? 0
    const len = want && remaining > 0.5
      ? (want.backward ? Math.min(suriSettings.stepPx, remaining) : remaining >= suriSettings.stepPx - 10
          ? Math.min(suriSettings.stepPx, remaining + 10) : needed > 0 ? Math.min(suriSettings.stepPx, Math.max(remaining, needed)) : 0)
      : 0
    if (want && len > 0.5 && elapsedMs >= (phase?.phase === 'draw' ? phase.durMs : 0)) {
      const speedMul = want.backward ? stepBackSpeedMul : 1
      const durMs = suriSettings.glideMs / speedMul * (len / suriSettings.stepPx)
      const glide = { phase: 'glide' as const, fromX: boss.x, toX: boss.x + want.worldDir * len,
        elapsedMs: 0, durMs, backward: want.backward }
      // 歩き始め (前へ) は立ち姿から寄せた足の構えへ腰を落とし、止まる時間だけ構えてから最初の滑り (1 tick だと構えが見えない)。
      //   後退 (重なりの立て直し) は待たずに滑る。
      if (!prev.walking && !want.backward) return { x: boss.x, gait: { s: prev.s, walking: true, restMs: 0,
        suri: { ...glide, phase: 'draw', elapsedMs: 0, durMs: suriSettings.restMs } }, blocksAttack: false }
      return { x: boss.x, gait: { s: prev.s, walking: true, restMs: 0, suri: glide }, blocksAttack: true }
    }
    const restMs = want ? 0 : prev.restMs + dtMs
    const walking = prev.walking && restMs < STEP_IDLE_AFTER_MS
    return { x: boss.x, gait: { s: prev.s, walking, restMs,
      ...(walking && phase ? { suri: { ...phase, elapsedMs } } : {}) }, blocksAttack: false }
  }
  if (mode === 'walk') {
    if (!want || want.remainingPx <= 0.01) {
      const restMs = prev.restMs + dtMs
      return { x: boss.x, gait: { s: prev.s, walking: prev.walking && restMs < WALK_IDLE_AFTER_MS, restMs }, blocksAttack: false }
    }
    const dx = want.worldDir * Math.min(want.remainingPx, want.speed * FIXED_DT)
    return { x: boss.x + dx, gait: { s: prev.walking ? prev.s : 0, walking: true, restMs: 0 }, blocksAttack: false }
  }
  if (mode === 'snap') {
    const stance: KnightStance = prev.walking ? prev.stance ?? 'idle' : 'idle'
    const restMs = want ? 0 : prev.restMs + dtMs
    if (!want || want.remainingPx <= 0.01) {
      const walking = stance !== 'idle' && restMs < STEP_IDLE_AFTER_MS
      return { x: boss.x, gait: { ...prev, walking, restMs, stance: walking ? stance : 'idle', snapHoldMs: walking ? prev.snapHoldMs : 0 }, blocksAttack: false }
    }
    // Alternating images are essential: a reach→reach jump would move the same planted bitmap.
    // Each jump locks the old support foot to the new support foot. No position changes on held ticks.
    const from = prev.snapBackward === want.backward ? stance : 'idle'
    const to: KnightStance = from === 'idle' && want.backward ? 'walk_passing'
      : from === 'walk_reach' ? 'walk_passing' : 'walk_reach'
    const px = want.backward ? knightStepBackPx(from, to) : knightStepForwardPx(from, to)
    const speed = want.speed * (want.backward ? stepBackSpeedMul : 1)
    const holdMs = from === 'idle' ? 0 : (px / Math.max(1, speed)) * 1000
    const elapsed = (prev.snapHoldMs ?? 0) + dtMs
    // The last step may pass the preferred point by the same 10px tolerance as step.
    // Never make a partial jump: that would move a planted foot off its floor point.
    const needed = Math.max(0, want.mustPx)
    const fits = want.backward || px <= want.remainingPx + 10 || (needed > 0 && px >= needed)
    if (elapsed >= holdMs && fits) {
      return { x: boss.x + want.worldDir * px,
        gait: { s: prev.s, walking: true, restMs: 0, stance: to, snapHoldMs: 0, snapBackward: want.backward },
        blocksAttack: true }
    }
    return { x: boss.x,
      gait: { s: prev.s, walking: from !== 'idle', restMs: 0, stance: from, snapHoldMs: elapsed, snapBackward: want.backward },
      blocksAttack: false }
  }
  // step
  if (prev.step) {
    const elapsedMs = prev.step.elapsedMs + dtMs
    const x = prev.step.fromX + (prev.step.toX - prev.step.fromX) * smoothstep(elapsedMs / prev.step.durMs)
    if (elapsedMs >= prev.step.durMs) {
      return { x, gait: { s: prev.s, walking: true, restMs: 0, stance: prev.step.to, ...stepComparisonState(prev) }, blocksAttack: false }
    }
    return { x, gait: { ...prev, step: { ...prev.step, elapsedMs } }, blocksAttack: true }
  }
  const stance: KnightStance = prev.walking ? prev.stance ?? 'idle' : 'idle'
  const restMs = prev.restMs + dtMs
  const next = want ? chooseKnightStep(stance, want) : null
  // 歩き始め (立ち姿から) は待たずに出る。歩いている途中は、止まる時間を置いてから次の1歩。
  const directionChanged = !!want && (prev.stepWorldDir !== want.worldDir || prev.stepBackward !== want.backward)
  const count = prev.walking && !directionChanged ? prev.stepCount ?? 0 : 0
  const burstPause = !want?.backward && burstSteps !== undefined && count > 0 && count % burstSteps === 0 ? burstRestMs : 0
  const restNeeded = stance === 'idle' ? 0 : stepComparisonEnabled()
    ? want ? stepRestMs(want) + burstPause : prev.stepRestNeededMs ?? stepRestMs(want)
    : stepRestMs(want)
  if (want && next && next.px > 0.5 && restMs >= restNeeded) {
    const durMs = stepMoveMs(want, next.px)
    const step = { fromX: boss.x, toX: boss.x + want.worldDir * next.px, elapsedMs: dtMs, durMs, to: next.to,
      ...(openWalk() || reachWalk() ? { from: stance, backward: want.backward } : {}) }
    const x = step.fromX + (step.toX - step.fromX) * smoothstep(dtMs / durMs)
    return { x, gait: { s: prev.s, walking: true, restMs: 0, stance, step,
      ...(stepComparisonEnabled() ? { stepCount: count + 1, stepWorldDir: want.worldDir, stepBackward: want.backward,
        stepRestNeededMs: stepRestMs(want) + (!want.backward && burstSteps !== undefined && (count + 1) % burstSteps === 0 ? burstRestMs : 0) } : {}) }, blocksAttack: true }
  }
  const walking = stance !== 'idle' && restMs < Math.max(STEP_IDLE_AFTER_MS, restNeeded + 60)
  return {
    x: boss.x,
    gait: { s: prev.s, walking, restMs, stance: walking ? stance : 'idle', ...(walking ? stepComparisonState(prev) : {}) },
    blocksAttack: false,
  }
}

function stepComparisonState(g: KnightGait): Partial<KnightGait> {
  return stepComparisonEnabled() ? { stepCount: g.stepCount, stepWorldDir: g.stepWorldDir,
    stepBackward: g.stepBackward, stepRestNeededMs: g.stepRestNeededMs } : {}
}
function stepPeriodMs(want: KnightGaitWant): number {
  const speed = want.backward ? want.speed * stepBackSpeedMul : want.speed
  // Backward regain was tuned by bot in 09-27: comparison params only affect forward steps.
  if (!want.backward && forwardTempo !== undefined) return forwardTempo
  return (knightNaturalStridePx() / Math.max(1, speed)) * 1000
}
function stepRestMs(want: KnightGaitWant | null): number {
  if (!want) return 180
  if (mode === 'step' && quickMoveMs !== undefined) return Math.max(120, stepPeriodMs(want) - quickMoveMs)
  return stepPeriodMs(want) * (1 - STEP_MOVE_SHARE)
}
function stepMoveMs(want: KnightGaitWant, len: number): number {
  if (mode === 'step' && quickMoveMs !== undefined) {
    const full = want.backward ? knightNaturalStridePx() : forwardStridePx()
    return len >= full ? quickMoveMs : Math.max(90, quickMoveMs * Math.sqrt(len / full))
  }
  // 短い1歩 (半歩・構えるだけ) も同じ足運びで、時間は歩幅の平方根で縮める (短い一歩ほど相対的にゆっくり)。
  return Math.max(140, stepPeriodMs(want) * STEP_MOVE_SHARE * Math.sqrt(Math.min(1, len / (want.backward ? knightNaturalStridePx() : forwardStridePx()))))
}

/** クランプ後の x で歩きの状態を確定する。壁・主人公の芯で止められた1歩はそこで着地扱い。 */
export function settleKnightGait(boss: Boss, gait: KnightGait, clampedX: number, rawX: number): KnightGait {
  const facingSign = boss.facing === 'right' ? 1 : -1
  const s = gait.s + (clampedX - boss.x) * facingSign
  if (gait.step && Math.abs(clampedX - rawX) > 0.01) {
    return { s, walking: true, restMs: 0, stance: gait.step.to, ...stepComparisonState(gait) }
  }
  if (gait.suri?.phase === 'glide' && Math.abs(clampedX - rawX) > 0.01) {
    return { s, walking: true, restMs: 0, suri: { ...gait.suri, phase: 'draw', elapsedMs: 0 } }
  }
  return { ...gait, s }
}

/** 歩きの絵。null = 歩きの絵を出さない (立ち絵のまま)。 */
export function knightGaitPoseKey(boss: Boss): 'walk_passing' | 'walk_reach' | 'suri_open' | 'suri_draw' | null {
  if (knightRestPoseKey(boss)) return 'walk_reach'
  if (!usesKnightGait(boss) || boss.state !== 'idle' || !boss.knightGait?.walking) return null
  const g = boss.knightGait
  if (mode === 'suri') return g.suri?.phase === 'glide' ? 'suri_open' : 'suri_draw'
  if (mode === 'step' || mode === 'snap') {
    if (reachWalk()) return 'walk_reach'
    if (openWalk()) return g.step ? 'walk_reach' : 'walk_passing'
    // 運んでいる間 = 揃えかけの絵。立ち姿から後ろ足を引いて構えるだけの1歩も同じ。
    if (g.step) return 'walk_passing'
    return g.stance === 'walk_passing' || g.stance === 'walk_reach' ? g.stance : null
  }
  // walk: 歩き始め (s=0) は揃えかけの絵の真ん中。前へ進めば 19.4px で広い構え、後ろへでも 19.4px で広い構え。
  const natural = knightNaturalStridePx()
  const reachHold = knightStepForwardPx('walk_reach', 'walk_passing')
  const passingHold = natural - reachHold
  const u0 = reachHold + passingHold / 2
  const u = (((g.s + u0) % natural) + natural) % natural
  return u < reachHold ? 'walk_reach' : 'walk_passing'
}

/** Draw-only world offset. The logical position, shadow, spacing and hurtbox stay put. */
export function knightGaitDrawOffsetPx(boss: Boss): number {
  if (knightRestPoseKey(boss)) {
    // Match the first walking frame's existing alignment. No logical position,
    // gait timing or step length changes; the resting bitmap stays planted.
    if (openWalk() && walkAnchor === 'feet') {
      const f = knightWalkFootPx()
      return (f.idle.back - f.walk_reach.back) * (boss.facing === 'right' ? 1 : -1)
    }
    if (openWalk() || reachWalk()) {
      const h = knightWalkHelmetPx()
      return (h.idle - h.walk_reach) * (boss.facing === 'right' ? 1 : -1)
    }
    return 0
  }
  const step = boss.knightGait?.step
  if (reachWalk() && knightGaitPoseKey(boss) === 'walk_reach' && step?.from === 'idle') {
    const h = knightWalkHelmetPx()
    return (h.idle - h.walk_reach) * (1 - smoothstep(step.elapsedMs / step.durMs)) * (boss.facing === 'right' ? 1 : -1)
  }
  if (!openWalk() || knightGaitPoseKey(boss) !== 'walk_reach' || !step) return 0
  const from = step.from ?? 'idle'
  let oFrom: number, oTo: number
  if (walkAnchor === 'feet') {
    const feet = knightWalkFootPx()
    const opening = step.backward ? 'front' : 'back'
    const closing = step.backward ? 'back' : 'front'
    oFrom = feet[from][opening] - feet.walk_reach[opening]
    oTo = feet.walk_passing[closing] - feet.walk_reach[closing]
  } else {
    const helmet = knightWalkHelmetPx()
    oFrom = helmet[from] - helmet.walk_reach
    oTo = helmet.walk_passing - helmet.walk_reach
  }
  const o = oFrom + (oTo - oFrom) * smoothstep(step.elapsedMs / step.durMs)
  return o * (boss.facing === 'right' ? 1 : -1)
}

// URL の読み取りは最後 (上の let が初期化された後)。配布版はこの箱ごと消える = 常に既定 (step)。
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search)
  setKnightWalkPose(params.get('knightWalkPose') as KnightWalkPose)
  setKnightWalkAnchor(params.get('knightWalkAnchor') as KnightWalkAnchor)
  setKnightStepComparison(Number(params.get('knightStride')), Number(params.get('knightTempo')))
  const explicitPose = params.has('knightWalkPose')
  setKnightStepMoveComparison(
    params.has('knightStepMoveMs') ? Number(params.get('knightStepMoveMs')) : explicitPose ? undefined : KNIGHT_WALK_DEFAULT.moveMs,
    params.has('knightStepBurst') ? Number(params.get('knightStepBurst')) : explicitPose ? undefined : KNIGHT_WALK_DEFAULT.burst,
    params.has('knightStepBurstRestMs') ? Number(params.get('knightStepBurstRestMs')) : KNIGHT_WALK_DEFAULT.burstRestMs)
  const v = params.get('knightMove')
  if (v) setKnightMove(v as KnightMove)
  const back = params.get('knightBack')
  if (back) setKnightStepBackSpeedMul(Number(back))
  if (getKnightMove() === 'suri') setKnightSuriSettings({
    stepPx: Number(params.get('knightSuriStep') ?? KNIGHT_SURI_DEFAULT.stepPx),
    glideMs: Number(params.get('knightSuriGlideMs') ?? KNIGHT_SURI_DEFAULT.glideMs),
    restMs: Number(params.get('knightSuriRestMs') ?? KNIGHT_SURI_DEFAULT.restMs),
  })
}
